import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { KICK_INVOICE_SENDER_ADDRESS } from "@common/constants/invoices";
import { NATIVE_INVOICE_DEFAULT_PAGE_SIZE } from "@common/constants/nativeInvoice";
import { renderNativeInvoiceDocument } from "@common/helpers/nativeInvoiceDocumentHtml";
import { NATIVE_INVOICE_PAGE_SIZE_PX } from "@common/helpers/nativeInvoicePagination";
import { Entity } from "@common/types/entity";
import type { NativeInvoiceParty } from "@common/types/nativeInvoice";
import {
    computeNativeInvoiceLineAmount,
    computeNativeInvoiceTotals,
} from "./computeNativeInvoiceTotals";
import {
    EMAIL_PATTERN,
    invoiceToPartyBusinessName,
    nonEmptyStringOrNull,
} from "./nativeInvoiceFormHelpers";
import { storedLineAdjustmentFromLabeled } from "./nativeInvoicePerLineAdjustment";
import { NativeInvoiceFormState } from "./nativeInvoiceFormTypes";
import frameStyles from "../invoicePaperFrame.module.scss";
import { InvoicePreviewPager } from "./InvoicePreviewPager";
import { writeNativeInvoicePreviewHtml } from "./writeNativeInvoicePreviewHtml";
import { useInvoicePreviewPaperScale } from "./useInvoicePreviewPaperScale";
import styles from "./InvoiceDocumentPreview.module.scss";

const FLUSH_SCREEN_PAGE_CSS = `
@media screen {
    .page {
        border: none;
        border-radius: 0;
        box-shadow: none;
        margin: 0 auto;
    }
}
`;

function withFlushScreenPageCss(html: string): string {
    return html.replace("</style>", `${FLUSH_SCREEN_PAGE_CSS}</style>`);
}

interface Props {
    state: NativeInvoiceFormState;
    entity?: Entity;
    customerEmail: string;
    subtotal: number;
    total: number;
    savedBranding?: {
        logoUrl: string | null;
        monogram: string | null;
    } | null;
    senderEmail?: string | null;
    // Also scales the paper down to the viewport's height so the full page is
    // always visible without scrolling. Only enable where the viewport has a
    // definite height (the preview pane): in auto-height containers (the email
    // attachment thumbnail) the viewport's height follows the paper's, which
    // would feed the scale back into itself.
    fitToViewportHeight?: boolean;
    paperChrome?: "card" | "flush";
}

function partyAddress(
    source:
        | NativeInvoiceFormState["from"]
        | NativeInvoiceFormState["customer"]
        | null
        | undefined,
): NativeInvoiceParty["address"] {
    return {
        street: nonEmptyStringOrNull(source?.street),
        street2: nonEmptyStringOrNull(source?.street2),
        city: nonEmptyStringOrNull(source?.city),
        state: nonEmptyStringOrNull(source?.state),
        postalCode: nonEmptyStringOrNull(source?.postalCode),
        country: nonEmptyStringOrNull(source?.country),
    };
}

export const InvoiceDocumentPreview: React.FC<Props> = ({
    state,
    entity,
    customerEmail,
    subtotal,
    total,
    savedBranding,
    senderEmail = KICK_INVOICE_SENDER_ADDRESS,
    fitToViewportHeight = false,
    paperChrome = "card",
}) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [pageIndex, setPageIndex] = useState(0);
    const pageSize = state.pageSize ?? NATIVE_INVOICE_DEFAULT_PAGE_SIZE;

    const logoUrl =
        (state.logoPreviewUrl ? encodeURI(state.logoPreviewUrl) : null) ??
        savedBranding?.logoUrl ??
        entity?.logo ??
        null;
    const fromEmail = EMAIL_PATTERN.test(state.from.email)
        ? state.from.email
        : null;
    const toEmail = EMAIL_PATTERN.test(customerEmail) ? customerEmail : null;
    const {
        taxAmount,
        discountAmount,
        depositAmount,
        taxSign,
        discountSign,
        depositSign,
    } = computeNativeInvoiceTotals({
        lines: state.lines,
        tax: state.tax,
        taxMode: state.taxMode,
        discount: state.discount,
        discountMode: state.discountMode,
        deposit: state.deposit,
        depositMode: state.depositMode,
        isTaxPerLineItem: state.isTaxPerLineItem,
        isDiscountPerLineItem: state.isDiscountPerLineItem,
        isDepositPerLineItem: state.isDepositPerLineItem,
    });

    const preview = useMemo(() => {
        const rendered = renderNativeInvoiceDocument({
            currency: "usd",
            invoiceNumber: nonEmptyStringOrNull(state.invoiceNumber),
            invoiceDate: state.invoiceDate,
            dueDate: nonEmptyStringOrNull(state.dueDate),
            poNumber: nonEmptyStringOrNull(state.poNumber),
            termType: state.termType,
            customTerm: state.customTerm,
            from: {
                businessName: nonEmptyStringOrNull(state.from.businessName),
                name: nonEmptyStringOrNull(state.from.name),
                email: fromEmail,
                phoneNumber: nonEmptyStringOrNull(state.from.phoneNumber),
                taxId: nonEmptyStringOrNull(state.from.taxId),
                address: partyAddress(state.from),
            },
            to: {
                businessName: invoiceToPartyBusinessName({
                    displayName: state.customer?.name,
                    company: state.toCompany,
                }),
                company: nonEmptyStringOrNull(state.toCompany),
                name: nonEmptyStringOrNull(state.toFullName),
                email: toEmail,
                phoneNumber: nonEmptyStringOrNull(state.customer?.phoneNumber),
                taxId: nonEmptyStringOrNull(state.toTaxId),
                address: partyAddress(state.customer),
            },
            lines: state.lines.map((line) => {
                const lineAmount = computeNativeInvoiceLineAmount(line);
                return {
                    productOrService: nonEmptyStringOrNull(
                        line.productOrService,
                    ),
                    description: nonEmptyStringOrNull(line.description),
                    quantity: line.quantity,
                    unitAmount: line.unitAmount,
                    amount: lineAmount,
                    // Resolve the labeled per-line input (percent or amount,
                    // whichever mode is active) to a dollar value, matching
                    // buildNativeInvoiceDraftBody so the preview's per-line
                    // columns show the same figures the saved PDF will.
                    tax: state.isTaxPerLineItem
                        ? storedLineAdjustmentFromLabeled({
                              labeledValue: line.tax,
                              mode: state.taxMode,
                              lineAmount,
                          })
                        : 0,
                    discount: state.isDiscountPerLineItem
                        ? storedLineAdjustmentFromLabeled({
                              labeledValue: line.discount,
                              mode: state.discountMode,
                              lineAmount,
                          })
                        : 0,
                    deposit: state.isDepositPerLineItem
                        ? storedLineAdjustmentFromLabeled({
                              labeledValue: line.deposit,
                              mode: state.depositMode,
                              lineAmount,
                          })
                        : 0,
                };
            }),
            pageSize,
            footer: nonEmptyStringOrNull(state.footer),
            footerAlign: state.footerAlign,
            showPagination: state.showPagination,
            subtotal,
            total,
            tax: taxAmount,
            discount: discountAmount,
            deposit: depositAmount,
            taxSign,
            discountSign,
            depositSign,
            note: nonEmptyStringOrNull(state.note),
            terms: nonEmptyStringOrNull(state.terms),
            backgroundColor: state.backgroundColor,
            accentColor: state.accentColor,
            textColor: state.textColor,
            fontFamily: state.fontFamily,
            logoUrl,
            monogram: savedBranding?.monogram ?? null,
            initials: null,
            labelOverrides: state.labelOverrides,
            fieldSettings: state.fieldSettings,
            senderEmail,
            isTaxPerLineItem: state.isTaxPerLineItem,
            isDiscountPerLineItem: state.isDiscountPerLineItem,
            isDepositPerLineItem: state.isDepositPerLineItem,
            isTaxDisplayedPerLineItem: state.isTaxDisplayedPerLineItem,
            isDiscountDisplayedPerLineItem:
                state.isDiscountDisplayedPerLineItem,
            isDepositDisplayedPerLineItem: state.isDepositDisplayedPerLineItem,
        });
        return {
            ...rendered,
            html: withFlushScreenPageCss(rendered.html),
        };
    }, [
        state,
        pageSize,
        senderEmail,
        fromEmail,
        toEmail,
        subtotal,
        total,
        taxAmount,
        discountAmount,
        depositAmount,
        taxSign,
        discountSign,
        depositSign,
        logoUrl,
        savedBranding?.monogram,
    ]);

    const currentPageIndex = Math.min(
        pageIndex,
        Math.max(preview.pageCount - 1, 0),
    );
    const showPager = fitToViewportHeight && preview.pageCount > 1;
    const frameSize = {
        width: preview.pageBox.width,
        height: preview.pageBox.height,
    };
    const showPaperChrome = paperChrome === "card";

    const paperScale = useInvoicePreviewPaperScale({
        viewportRef,
        logicalPaper: NATIVE_INVOICE_PAGE_SIZE_PX[pageSize],
        showPager,
        fitToViewportHeight,
    });

    useLayoutEffect(
        function syncPreviewIframe() {
            const iframe = iframeRef.current;
            if (!iframe) {
                return;
            }
            writeNativeInvoicePreviewHtml(
                iframe,
                preview.html,
                currentPageIndex,
            );
        },
        [preview.html, currentPageIndex],
    );

    return (
        <div
            ref={viewportRef}
            className={classNames(styles.paperScaleViewport, {
                [styles.paperScaleViewportFitHeight]: fitToViewportHeight,
            })}
        >
            <div className={styles.paperStack} style={{ zoom: paperScale }}>
                <div
                    className={
                        showPaperChrome
                            ? classNames(frameStyles.paperChrome, {
                                  [frameStyles.paperChromeWithPager]: showPager,
                              })
                            : undefined
                    }
                >
                    <iframe
                        ref={iframeRef}
                        className={frameStyles.documentFrame}
                        title="Invoice preview"
                        sandbox="allow-same-origin"
                        style={{
                            width: frameSize.width,
                            height: frameSize.height,
                        }}
                    />
                </div>
                {showPager ? (
                    <InvoicePreviewPager
                        pageCount={preview.pageCount}
                        pageIndex={currentPageIndex}
                        onPageChange={setPageIndex}
                    />
                ) : null}
            </div>
        </div>
    );
};
